import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { promoteService as promoteServiceFn } from "@/lib/marketplace/promote.functions";
import { PROMOTION_PLANS, isFeatured, type PromotionPlanKey } from "@/lib/marketplace/promotions";
import { useT } from "@/i18n";

type ServiceLite = { id: string; title: string; featured_until?: string | null };

/**
 * Paid featuring. Publishing is free — this only buys visibility for N days.
 */
export function ServicePromoteDialog({
  service,
  open,
  onOpenChange,
}: {
  service: ServiceLite | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useT();
  const promote = useServerFn(promoteServiceFn);
  const qc = useQueryClient();
  const [plan, setPlan] = useState<PromotionPlanKey>("days_5");
  const [busy, setBusy] = useState(false);

  const featured = isFeatured(service?.featured_until);

  async function submit() {
    if (!service) return;
    setBusy(true);
    try {
      const res = await promote({ data: { serviceId: service.id, planKey: plan } });
      toast.success(
        t("mpsell.promote.featuredSuccess", { days: res.days, date: new Date(res.featuredUntil).toLocaleDateString() }),
      );
      await qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e);
      toast.error(msg || t("mpsell.promote.errorDefault"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("mpsell.promote.title")}</DialogTitle>
          <DialogDescription>
            {t("mpsell.promote.description")}
          </DialogDescription>
        </DialogHeader>

        {featured && service?.featured_until && (
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            {t("mpsell.promote.currentlyFeatured", { date: new Date(service.featured_until).toLocaleDateString() })}
          </p>
        )}

        <div className="grid gap-3">
          {PROMOTION_PLANS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPlan(p.key)}
              className={`flex items-start justify-between gap-3 rounded-xl border p-4 text-left transition-colors ${
                plan === p.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
            >
              <span>
                <span className="block font-display text-base font-bold">{t(p.labelKey)}</span>
                <span className="block text-xs text-muted-foreground">{t(p.blurbKey)}</span>
              </span>
              <span className="whitespace-nowrap font-display text-lg font-bold text-primary">
                {p.heat} Heat
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <Button variant="hot" className="w-full" disabled={busy} onClick={() => void submit()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {t("mpsell.promote.payAndFeature")}
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/wallets">{t("mpsell.promote.topUpWallet")}</Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
